import { apiBaseUrl } from './baseUrl';

// ─────────────────────────────────────────────────────────────────────
// FINDING A CLUB BY NAME (#742).
//
// `GET /clubs?q=` — public, because the two screens that need it most have no
// credential: choosing a club happens BEFORE a code is requested.
//
// WHY A TRANSPORT FUNCTION FOR ONE FETCH. Because the three screens that ask
// for a club would otherwise each grow their own, and the one that got it
// slightly wrong would be the one nobody tested. The same reason the screens
// share a picker rather than a pattern.
//
// IT NEVER THROWS AND NEVER REPORTS AN ERROR. A lookup that fails is an empty
// list: the field still accepts what was typed, because a slug typed by hand
// must keep working when the directory is unreachable. Telling somebody «we
// could not search» while they are typing is noise about a thing they did not
// ask for.
// ─────────────────────────────────────────────────────────────────────

export interface ClubSuggestion {
  slug: string;
  name: string;
  /** `CENTRE` or `CLUB`. Returned so a screen can label it; not a filter. */
  kind: string;
}

/**
 * The outcome of one directory lookup.
 *
 * ── «NO MATCHES» AND «WE COULD NOT ASK» ARE DIFFERENT ANSWERS ─────
 *
 * `searchClubs` below returns `[]` for an absent API base, an HTTP error, a
 * malformed body AND a network failure — deliberately, because the two
 * original callers are type-ahead pickers where a failed keystroke should
 * simply show nothing. A SCREEN that reports the outcome cannot use it: an
 * outage renders «Ingen treff», which tells somebody the club they are
 * looking for does not exist (#832 review).
 *
 * So the mapping is exposed rather than inferred. `searchClubs` keeps its
 * shape for the pickers, and anything that reports the difference asks for
 * the outcome instead.
 *
 * `aborted` IS ITS OWN CASE and is not a failure: a superseded keystroke is
 * the normal way a type-ahead ends, and showing «the search did not answer»
 * for it would make every fast typist see an error.
 *
 * A STRING discriminant rather than `ok: true | false`. This repo's
 * `tsconfig.base.json` sets `"strict": false`, and boolean-literal unions do
 * not narrow under it — `if (!outcome.ok)` leaves the compiler believing the
 * success member is still possible. `status` narrows either way.
 */
export type ClubSearchOutcome =
  { status: 'ok'; clubs: ClubSuggestion[] } | { status: 'aborted' } | { status: 'unavailable' };

const UNAVAILABLE = { status: 'unavailable' } as const;

export async function searchClubsOutcome(
  query: string,
  signal?: AbortSignal,
): Promise<ClubSearchOutcome> {
  const base = apiBaseUrl();
  if (!base) return UNAVAILABLE;
  try {
    const res = await fetch(`${base}/clubs?q=${encodeURIComponent(query)}`, { signal });
    if (!res.ok) return UNAVAILABLE;
    const body = (await res.json()) as { clubs?: unknown };
    if (!Array.isArray(body.clubs)) return UNAVAILABLE;
    return { status: 'ok', clubs: body.clubs.flatMap(toSuggestion) };
  } catch {
    // THE SIGNAL, NOT THE ERROR'S NAME. `fetch` rejects with `AbortError` in
    // a browser and jsdom, but a `signal` aborted between the two awaits can
    // surface as something else; the signal is the thing that is true.
    if (signal?.aborted) return { status: 'aborted' };
    return UNAVAILABLE;
  }
}

/**
 * The directory as the type-ahead pickers want it: a list, never a throw, and
 * an empty list for every failure. `ClubPicker` and `ClubDiscoveryScreen`
 * depend on this shape — including an aborted request resolving `[]`.
 */
export async function searchClubs(query: string, signal?: AbortSignal): Promise<ClubSuggestion[]> {
  const outcome = await searchClubsOutcome(query, signal);
  return outcome.status === 'ok' ? outcome.clubs : [];
}

function toSuggestion(row: unknown): ClubSuggestion[] {
  const r = row as { slug?: unknown; name?: unknown; kind?: unknown };
  // A SUGGESTION WITHOUT A SLUG IS NOT ONE — picking it would send the empty
  // string to the join endpoint.
  if (typeof r.slug !== 'string' || r.slug === '') return [];
  return [
    {
      slug: r.slug,
      name: typeof r.name === 'string' && r.name ? r.name : r.slug,
      kind: typeof r.kind === 'string' ? r.kind : '',
    },
  ];
}
